import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Tables } from "@/gen/tableNames/tables";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  CheckboxField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";
import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";

import { fundEsgRatingOptions, subAssetClassOptions } from "./filter";

// Zod schema: make fields optional and nullable to match the form behavior
const FormSchema = z.object({
  strategy_id: z.coerce.number().optional().nullable(),
  company_name: z.string().nullable().optional(),
  isin: z.string().nullable().optional(),
  strategy_name: z.string().nullable().optional(),
  commitment_fgao: z.coerce.number().optional().nullable(),
  commitment_fgti: z.coerce.number().optional().nullable(),
  commitment_fgao_rentes: z.coerce.number().optional().nullable(),
  dedicated_fund: z.boolean().optional().nullable(),
  exemption_comment: z.string().nullable().optional(),
  sub_asset_class: z.string().nullable().optional(),
  fund_esg_rating_level: z.coerce.number().optional().nullable(),
  created_at: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  updated_at: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export function F1StrategyMetadataEditCard({
  id,
  client,
}: {
  id: number;
  client: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    id,
    client,
    table_name: Tables.F1_STRATEGY_METADATA,
    FormSchema,
    disable_translations_request: true,
    adjustOnReset: (key, value) => {
      if (key == "fund_esg_rating_level") {
        return value.toString();
      }
      return value;
    },
  });

  const strategyName = form.watch("strategy_name");
  const metadataTitle = t(`${Tables.F1_STRATEGY_METADATA}.title`, {
    ns: "tables",
    defaultValue: "F1 Strategy Metadata",
  });
  const cardTitle = strategyName
    ? `${metadataTitle} "${strategyName}"`
    : `${metadataTitle} ${id}`;

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {cardTitle}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* General Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="strategy_name"
                    label={t(
                      `${Tables.F1_STRATEGY_METADATA}.columns.strategy_name`,
                      { ns: "tables" },
                    )}
                    control={form.control}
                    fieldOptions={{ isReadOnly: true }}
                  />
                  <InputField
                    name="isin"
                    label={t(`${Tables.F1_STRATEGY_METADATA}.columns.isin`, {
                      ns: "tables",
                    })}
                    control={form.control}
                    fieldOptions={{ isReadOnly: true }}
                  />
                  <InputField
                    name="company_name"
                    label={t(
                      `${Tables.F1_STRATEGY_METADATA}.columns.company_name`,
                      { ns: "tables" },
                    )}
                    control={form.control}
                    fieldOptions={{ isReadOnly: true }}
                  />
                  <CheckboxField
                    name="dedicated_fund"
                    label={t(
                      `${Tables.F1_STRATEGY_METADATA}.columns.dedicated_fund`,
                      {
                        ns: "tables",
                        defaultValue: "Fonds Dédié",
                      },
                    )}
                    control={form.control}
                  />
                </div>

                {/* Sub Asset Class */}
                <div className="grid grid-cols-1 gap-4">
                  <SelectField
                    name="sub_asset_class"
                    label={t(
                      `${Tables.F1_STRATEGY_METADATA}.columns.sub_asset_class`,
                      {
                        ns: "tables",
                        defaultValue: "Sous-classe d'Actifs",
                      },
                    )}
                    options={subAssetClassOptions}
                    control={form.control}
                    allow_empty_option={true}
                  />
                </div>

                {/* Commitments */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField
                    name="commitment_fgao"
                    label={t(
                      `${Tables.F1_STRATEGY_METADATA}.columns.commitment_fgao`,
                      {
                        ns: "tables",
                        defaultValue: "Engagement FGAO",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="commitment_fgti"
                    label={t(
                      `${Tables.F1_STRATEGY_METADATA}.columns.commitment_fgti`,
                      {
                        ns: "tables",
                        defaultValue: "Engagement FGTI",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="commitment_fgao_rentes"
                    label={t(
                      `${Tables.F1_STRATEGY_METADATA}.columns.commitment_fgao_rentes`,
                      {
                        ns: "tables",
                        defaultValue: "Engagement FGAO Rentes",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                </div>

                {/* ESG Rating */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="fund_esg_rating_level"
                    label={t(
                      `${Tables.F1_STRATEGY_METADATA}.columns.fund_esg_rating_level`,
                      {
                        ns: "tables",
                        defaultValue: "Fund ESG Rating",
                      },
                    )}
                    options={fundEsgRatingOptions}
                    control={form.control}
                    allow_empty_option={true}
                  />
                  <InputField
                    name="exemption_comment"
                    label={t(
                      `${Tables.F1_STRATEGY_METADATA}.columns.exemption_comment`,
                      {
                        ns: "tables",
                        defaultValue: "Exemption Comment",
                      },
                    )}
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation before creating new item */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields ? Diff(changedFields, []) : "No changes detected"}
          </ConfirmationModal>

          {/* Cancel (navigate back) confirmation */}
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
